import { assertPresetAllowed, awaitPresetAllowed } from '../access.js';
import { presetActionReceipt } from '../analytics.js';
import { normalizeId } from '@studio/models';
import { Limits } from '@studio/models/control/preset';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import { StatusDeviceNativePresetMode } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StudioDeviceControlPreset } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { isEmpty, toString } from '@tryiris-ai/js-types';
import type { UpdatePreset } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import {
  finishPresetSave,
  freezePresetDevice,
  jsNativeTargetToPb,
  pbNativeTargetToJs,
  runNativePresetSave,
  serializeDevicePresetSave,
  usesNativeSaveOwner
} from '#~/control/preset/native-owner.js';
import { StudioError } from '#~/error/index.js';

/**
 * Update an existing device preset.
 *
 * Rename/reorder stay metadata-only. A new captured shot reuses the native
 * mapping when the encoder is in native mode.
 */
export const updatePreset: UpdatePreset = async (deviceId, presetId, updates, token) => {
  // A recapture picks legacy or native from capabilities; wait for them.
  if (updates.position) await awaitPresetAllowed(deviceId);
  const accepted = updates.position ? presetActionReceipt(deviceId, 'save') : undefined;
  return serializeDevicePresetSave(deviceId, async () => {
    try {
      const proxy = locateProxy(deviceId);

      if (!proxy) {
        throw new Error('UP-694');
      }

      const frozen = freezePresetDevice(proxy, deviceId);
      const assertDevice: typeof frozen = () => {
        if (updates.position) assertPresetAllowed(deviceId);
        frozen();
      };
      assertDevice();

      const mutation: DeepPartial<StudioDeviceControlPreset> = {};
      const groupId = normalizeId(updates.groupId);

      if (groupId) {
        mutation.group = groupId;
      }

      const name = toString(updates.name);

      if (name) {
        mutation.name = name.slice(0, Limits.maxNameLength);
      }

      if (updates.position && !usesNativeSaveOwner(proxy, deviceId)) {
        // Legacy/excluded update callers supply the shot themselves. Status is
        // neither a replacement for that pose nor a prerequisite for this edit.
        mutation.position = { ...updates.position };
        mutation.requiredAxes = 0;
      } else if (updates.position) {
        const existing =
          proxy.state.control?.studio?.device?.[deviceId]?.control?.presets?.items?.[presetId];
        const reuse = pbNativeTargetToJs(existing?.nativeTarget);
        const { result, position } = await runNativePresetSave(
          deviceId,
          presetId,
          name || existing?.name || '',
          reuse,
          token
        );

        assertDevice();
        if (result?.failure && !result.completed) {
          throw new StudioError(result.failure);
        }

        const useNative =
          result?.completed &&
          result.mode === StatusDeviceNativePresetMode.NATIVE_PRESET_MODE_NATIVE &&
          result.target;

        if (useNative) {
          mutation.nativeTarget = jsNativeTargetToPb(result.target);
          mutation.requiredAxes = 0;
        } else {
          if (!position)
            throw new StudioError('Unable to save preset without a qualified position.');
          mutation.position = position;
          mutation.requiredAxes = result?.requiredAxes;
        }
      }

      if (isEmpty(mutation)) {
        return;
      }

      assertDevice();
      await proxy.mutate(
        {
          control: {
            studio: {
              device: {
                [deviceId]: {
                  control: {
                    presets: {
                      items: {
                        [presetId]: mutation
                      }
                    }
                  }
                }
              }
            }
          }
        },
        updates.position
          ? {
              control: {
                studio: {
                  device: {
                    [deviceId]: {
                      control: {
                        presets: {
                          items: {
                            [presetId]: {
                              ...(mutation.position ? { nativeTarget: {} } : { position: {} })
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          : {}
      );

      assertDevice();
      await proxy.save(token);
      assertDevice();
      if (updates.position) finishPresetSave(deviceId, presetId);
      accepted?.();
    } catch (cause) {
      if (cause instanceof StudioError) {
        throw cause;
      }
      throw new StudioError('Unable to update preset.', { cause });
    }
  });
};
